import React, { FC, useEffect, useState } from 'react'
import { Trans } from '@lingui/macro'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { groupVideosByDate } from '../../../api/channel/getVideosForChannel'
import {
  getAllUpcomingVideos,
  GetAllUpcomingVideosProps
} from '../../../api/video/getAllUpcomingVideos'
import {
  getFilteredUpcomingStreams,
  GetFilteredUpcomingStreamsProps
} from 'api/video/getFilteredUpcomingStreams'
import { mapBroadcastThumbnailResponse } from '../../../api/channel/mapBroadcastThumnbailFromResponse'
import ChronologicalVideoList from '../../../components/lists/ChronologicalVideoList/ChronologicalVideoList'
import { channelSelector } from '../../../redux-store/channel/channel.selectors'
import LoadMoreSection from '../../../components/LoadMoreSection/LoadMoreSection'
import { Loader } from '../../../components/Loader/Loader'
import { ChannelTypes } from 'pages/ChannelPage/channelType'
import {
  getScheduleContentForChannel,
  GetScheduleContentForChannelProps
} from 'api/channel/getScheduleContentForChannel'
import { TimelineObject } from 'interfaces'
import { useInfiniteQueryDataFetcher } from 'hooks/useInfiniteQueryDataFetcher/useInfiniteQueryDataFetcher'

import styles from './UpcomingStreams.module.scss'

type QueryParams =
  | GetAllUpcomingVideosProps
  | GetFilteredUpcomingStreamsProps
  | GetScheduleContentForChannelProps

interface Props {
  showAllGames: boolean
  showEmptyMessage: boolean
}
const PER_PAGE_COUNT = 15

export const UpcomingStreams: FC<Props> = ({
  showAllGames,
  showEmptyMessage
}) => {
  const { company } = useParams<{ company?: string }>()
  const channel = useSelector(channelSelector)
  const channelType = channel?.data?.type
  const subdomain = channel?.data?.subdomain

  const isCurrentChannel = subdomain && subdomain === company
  const isRootChannel = isCurrentChannel && channel?.data?.isRootChannel
  const isChildOfRootChannel =
    isCurrentChannel && channel?.data?.isChildOfRootChannel

  const isRootOrChild = isRootChannel || isChildOfRootChannel
  const isEventPage = [ChannelTypes.event].includes(channelType)

  const [videoData, setVideoData] = useState<Record<string, any>>({})
  const videoDataLength = Object.keys(videoData).length

  const apiToUse = (params: QueryParams) => {
    if (showAllGames) {
      return getAllUpcomingVideos(params as GetAllUpcomingVideosProps)
    }
    if (isEventPage) {
      return getScheduleContentForChannel(
        params as GetScheduleContentForChannelProps
      )
    }
    return getFilteredUpcomingStreams(params as GetFilteredUpcomingStreamsProps)
  }

  const queryParameters: QueryParams = {
    ...(isRootChannel && !isEventPage
      ? { rootChannelSlug: company }
      : { slug: company }),
    perPageCount: PER_PAGE_COUNT,
    page: 1
  }

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    isFetchingNextPage,
    currentPage,
    totalPages
  } = useInfiniteQueryDataFetcher<QueryParams, TimelineObject>({
    queryKey: 'CalendarUpcomingStreams',
    queryFn: apiToUse,
    params: queryParameters,
    perPage: PER_PAGE_COUNT
  })

  useEffect(() => {
    if (!isLoading && !isError && data && data?.length > 0) {
      const mappedVideos = mapBroadcastThumbnailResponse(data)
      const groupedVideos = groupVideosByDate(mappedVideos)
      // setVideoData((prevData) => mergeVideoDataObjects(prevData, groupedVideos))
      setVideoData(groupedVideos)
    }
  }, [isLoading, isError, data.length, isRootOrChild, subdomain, showAllGames])

  if (isLoading && videoDataLength < 1)
    return (
      <div className={styles.GameSchedulePageContainer}>
        <Loader />
      </div>
    )

  if (isError)
    return (
      <h4>
        <Trans>
          Something went wrong trying to get the list of upcoming streams please
          try again
        </Trans>
      </h4>
    )

  if (videoDataLength === 0 && showEmptyMessage)
    return (
      <div className={styles.GameSchedulePageContainerEmpty}>
        <Trans>There are no upcoming streams right now</Trans>
      </div>
    )

  return (
    <div
      data-testid="upcoming-streams"
      className={styles.GameSchedulePageContainer}
    >
      <ChronologicalVideoList list={videoData} />
      {isFetchingNextPage && <Loader />}
      <LoadMoreSection
        onClick={() => fetchNextPage()}
        totalPages={totalPages}
        currentPage={currentPage}
      />
    </div>
  )
}
